import * as _ from 'lodash';
import { NgForage } from 'ngforage';
import { partial } from 'ramda';
import {
  BehaviorSubject,
  combineLatest,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { debounceTime, mergeMap } from 'rxjs/operators';
import AssetHelper from 'src/app/helpers/asset-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { ObservableHelper } from 'src/app/helpers/observable-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Asset from 'src/app/model/asset';
import ProductAsset from 'src/app/model/product-asset';
import BoardType from 'src/app/model/product/board/board-type';
import PlacementType from 'src/app/model/product/board/placement-type';
import Product from 'src/app/model/product/product';
import Address from 'src/app/model/project/address';
import { ProjectPlacement } from 'src/app/model/project/projectPlacement';
import {
  SubProject,
  SUB_PROJECT_TYPES,
} from 'src/app/model/project/sub-project';
import RegisteredAction from 'src/app/model/registered-action';
import {
  ImmoIPProductChangesObject,
  isProductToDoItemProp,
} from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes-object';
import { ImmoIPProductChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes.service';
import { ImmoIPNotExecutedHistoryItemObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-not-executed-history-item-object';
import {
  ImmoIPSubProjectChangesObject,
  isSubProjectToDoItemProp,
} from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes-object';
import { ImmoIPSubProjectChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes.service';
import { ToDoItem } from 'src/app/services/immo-ip-placer-offline-changes/model/to-do-item';
import {
  ImmoIPPlacerSubmitQueueService,
  ImmoIPSubmitType,
} from 'src/app/services/immo-ip-placer-submit/immo-ip-placer-submit-queue.service';
import { ImmoIPPlacerSubmitService } from 'src/app/services/immo-ip-placer-submit/immo-ip-placer-submit.service';
import { OfflineAsset } from 'src/app/services/placer-offline-changes/offline-asset';
import { SnackService } from 'src/app/services/snack.service';
import { OfflineStorageTourModifiedConfigHelper } from '../../tour-view/offline-storage/helper/offline-storage-tour-modified-config-helper';

export class ImmoIPPlacerTourItemDetailChangeStorageHandler {
  private static STORE_OFFLINE_CHANGE_DELAY = 1000;

  static async initialize(
    subProject: SubProject,
    dateString: string,
    ngForage: NgForage,
    immoIPSubProjectChangesService: ImmoIPSubProjectChangesService,
    immoIPProductChangesService: ImmoIPProductChangesService,
    immoIPPlacerSubmitService: ImmoIPPlacerSubmitService,
    immoIPPlacerSubmitQueueService: ImmoIPPlacerSubmitQueueService,
    snackService: SnackService
  ): Promise<ImmoIPPlacerTourItemDetailChangeStorageHandler> {
    let subProjectChanges = await this.initializeSubProjectChanges(
      subProject,
      immoIPSubProjectChangesService,
      immoIPProductChangesService
    );

    let offlineAssetsSet = new Set<string>();

    if (
      subProject.projectPlacement != null &&
      CollectionHelper.isPopulated(subProjectChanges.offlineAssets)
    ) {
      await this.handleOfflineProjectPlacementAssets(
        subProject.projectPlacement,
        subProjectChanges.offlineAssets,
        offlineAssetsSet,
        ngForage
      );
    }

    const offlineSubProjectStoreTriggerSubject =
      new Subject<ImmoIPSubProjectChangesObject>();
    const storingSubProjectChange = new Subject<boolean>();

    let productChangesMap: Map<number, ImmoIPProductChangesObject> = null;
    let productChangesStoreTriggerSubjects: Map<
      number,
      Subject<ImmoIPProductChangesObject>
    > = null;
    let storingProductChange: Map<number, Subject<boolean>> = null;

    if (
      subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
      subProject.project != null &&
      CollectionHelper.isPopulated(subProject.project.products)
    ) {
      const productChangeVariables: ProductChangeVariables =
        await this.initializeProductChangeVariables(
          subProject,
          immoIPProductChangesService,
          offlineAssetsSet,
          ngForage
        );
      productChangesMap = productChangeVariables.productChangesMap;
      productChangesStoreTriggerSubjects =
        productChangeVariables.productChangesStoreTriggerSubjects;
      storingProductChange = productChangeVariables.storingProductChange;
    }

    return new ImmoIPPlacerTourItemDetailChangeStorageHandler(
      dateString,
      subProjectChanges,
      offlineSubProjectStoreTriggerSubject,
      storingSubProjectChange,
      productChangesMap,
      productChangesStoreTriggerSubjects,
      storingProductChange,
      offlineAssetsSet,
      ngForage,
      immoIPSubProjectChangesService,
      immoIPProductChangesService,
      immoIPPlacerSubmitService,
      immoIPPlacerSubmitQueueService,
      snackService
    );
  }

  private static async initializeSubProjectChanges(
    subProject: SubProject,
    immoIPSubProjectChangesService: ImmoIPSubProjectChangesService,
    immoIPProductChangesService: ImmoIPProductChangesService
  ): Promise<ImmoIPSubProjectChangesObject> {
    let toReturn = await immoIPSubProjectChangesService.getObject(
      subProject.id
    );
    // if the tourItem id differs from what is stored locally, we are certain that the subproject has been reassigned,
    // therefore any changes that may have been stored locally are no longer valid and should be deleted
    if (
      toReturn != null &&
      subProject.tourItem != null &&
      subProject.tourItem.id != null &&
      toReturn.tourItemId !== subProject.tourItem.id
    ) {
      await this.deleteLocalChanges(
        subProject,
        toReturn,
        immoIPSubProjectChangesService,
        immoIPProductChangesService
      );
      toReturn = null;
    }
    if (toReturn == null) {
      toReturn = {
        id: subProject.id,
        tourItemId: subProject.tourItem == null ? null : subProject.tourItem.id,
        projectId: subProject.project.id,
        internalInfo: subProject.internalInfo,
        recurringInternalInfo: subProject.recurringInternalInfo,
      };
    }

    toReturn.isChange = false;
    if (
      subProject.latestNotExecutedHistoryItem != null &&
      subProject.tourItem != null &&
      subProject.tourItem.id != null &&
      subProject.tourItem.id ===
        subProject.latestNotExecutedHistoryItem.tourItemId
    ) {
      toReturn.notExecutedHistoryItemObject =
        subProject.latestNotExecutedHistoryItem;
    }

    if (subProject.type !== SUB_PROJECT_TYPES.BASE_PROJECT) {
      const assets =
        subProject.projectPlacement != null &&
        CollectionHelper.isPopulated(
          subProject.projectPlacement.projectPlacementAssets
        )
          ? subProject.projectPlacement.projectPlacementAssets.filter(
              (a) => a.formSection === 'adjustmentPlacementSuccess'
            )
          : null;
      if (CollectionHelper.isPopulated(assets)) {
        toReturn.assets = assets;
      }
    }

    return toReturn;
  }

  private static async deleteLocalChanges(
    subProject: SubProject,
    subProjectChanges: ImmoIPSubProjectChangesObject,
    immoIPSubProjectChangesService: ImmoIPSubProjectChangesService,
    immoIPProductChangesService: ImmoIPProductChangesService
  ): Promise<void> {
    if (
      subProject.project != null &&
      CollectionHelper.isPopulated(subProject.project.products)
    ) {
      const productIds = subProject.project.products.map((p) => p.id);
      CollectionHelper.filterInPlace(productIds, (id) => id != null);
      const productChangeObjects = await immoIPProductChangesService.getObjects(
        productIds
      );
      if (CollectionHelper.isPopulated(productChangeObjects)) {
        await immoIPProductChangesService.removeObjects(productChangeObjects);
      }
    }
    await immoIPSubProjectChangesService.removeObject(subProjectChanges);
  }

  private static async initializeProductChangeVariables(
    subProject: SubProject,
    immoIPProductChangesService: ImmoIPProductChangesService,
    offlineAssetsSet: Set<string>,
    ngForage: NgForage
  ): Promise<ProductChangeVariables> {
    const productChangesStoreTriggerSubjects = new Map<
      number,
      Subject<ImmoIPProductChangesObject>
    >();
    const productIds = subProject.project.products.map((p) => p.id);
    CollectionHelper.filterInPlace(productIds, (id) => id != null);
    const productChangeObjects = await immoIPProductChangesService.getObjects(
      productIds
    );
    const productChangesMap = new Map<number, ImmoIPProductChangesObject>(
      productChangeObjects.map((o) => [o.id, o])
    );
    const storingProductChange = new Map<number, Subject<boolean>>(
      productIds.map((id) => [id, new Subject<boolean>()])
    );
    const isBase = subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT;
    for (const p of subProject.project.products) {
      await this.handleProductChangeVariablesForProduct(
        productChangesStoreTriggerSubjects,
        p,
        productChangesMap,
        offlineAssetsSet,
        ngForage,
        isBase
      );
    }
    return {
      productChangesMap: productChangesMap,
      productChangesStoreTriggerSubjects: productChangesStoreTriggerSubjects,
      storingProductChange: storingProductChange,
    };
  }

  private static async handleProductChangeVariablesForProduct(
    productChangesStoreTriggerSubjects: Map<
      number,
      Subject<ImmoIPProductChangesObject>
    >,
    p: Product,
    productChangesMap: Map<number, ImmoIPProductChangesObject>,
    offlineAssetsSet: Set<string>,
    ngForage: NgForage,
    isBase: boolean
  ) {
    productChangesStoreTriggerSubjects.set(
      p.id,
      new Subject<ImmoIPProductChangesObject>()
    );

    let changeObject: ImmoIPProductChangesObject = productChangesMap.get(p.id);
    if (changeObject == null) {
      changeObject = {
        id: p.id,
      };
      productChangesMap.set(p.id, changeObject);
    }

    if (ProductHelper.isIPBoard(p)) {
      const assets = CollectionHelper.isPopulated(p.productAssets)
        ? p.productAssets.filter((a) => a.formSection === 'execution')
        : null;
      if (CollectionHelper.isPopulated(assets)) {
        productChangesMap.set(p.id, { ...changeObject, assets: assets });
      }
    }
    if (CollectionHelper.isPopulated(changeObject.offlineAssets)) {
      await this.handleOfflineProductAssets(
        p,
        changeObject.offlineAssets,
        offlineAssetsSet,
        ngForage
      );
    }
  }

  private static async handleOfflineProductAssets(
    product: Product,
    offlineAssets: OfflineAsset[],
    offlineAssetsSet: Set<string>,
    ngForage: NgForage
  ): Promise<void> {
    if (product.productAssets == null) {
      product.productAssets = [];
    }
    for (const oa of offlineAssets) {
      const offlineProductAsset = await this.instantiateOfflineAsset(
        oa,
        ngForage
      );
      product.productAssets.push(offlineProductAsset);
      offlineAssetsSet.add(oa.localFileUuid);
    }
    this.filterDuplicateOfflineAssets(product.productAssets);
  }

  private static async handleOfflineProjectPlacementAssets(
    projectPlacement: ProjectPlacement,
    offlineAssets: OfflineAsset[],
    offlineAssetsSet: Set<string>,
    ngForage: NgForage
  ): Promise<void> {
    if (projectPlacement.projectPlacementAssets == null) {
      projectPlacement.projectPlacementAssets = [];
    }
    for (const oa of offlineAssets) {
      const offlineProjectPlacementAsset = await this.instantiateOfflineAsset(
        oa,
        ngForage
      );
      projectPlacement.projectPlacementAssets.push(
        offlineProjectPlacementAsset
      );
      offlineAssetsSet.add(oa.localFileUuid);
    }
    this.filterDuplicateOfflineAssets(projectPlacement.projectPlacementAssets);
  }

  private static async instantiateOfflineAsset(
    offlineAsset: OfflineAsset,
    ngForage: NgForage
  ): Promise<ProductAsset> {
    const productAsset = new ProductAsset();
    const asset = new Asset();
    productAsset.asset = asset;
    productAsset.formSection = offlineAsset.formSection;
    asset.uuid = offlineAsset.uuid;
    asset.name = offlineAsset.name;
    asset.type = offlineAsset.type;
    asset.localFileUuid = offlineAsset.localFileUuid;
    const localFile = await ngForage.getItem<File>(
      AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
    );
    asset.localFile = localFile;
    return productAsset;
  }

  private static filterDuplicateOfflineAssets(productAssets: ProductAsset[]) {
    if (!CollectionHelper.isPopulated(productAssets)) {
      return;
    }
    const copy = [...productAssets];
    CollectionHelper.filterInPlace(
      productAssets,
      (pa1, predicateArgs) =>
        pa1.asset != null &&
        !predicateArgs[0].some(
          (pa2) =>
            pa1 !== pa2 &&
            pa2.asset != null &&
            pa2.asset.uuid != null &&
            pa1.asset.uuid === pa2.asset.uuid &&
            pa2.asset.localFileUuid != null
        ),
      copy
    ); // prefer to show offline assets
  }

  changeInstallerFeedback: (o: ImmoIPProductChangesObject, s: string) => void =
    (o, s) => {
      o.installerFeedback = s;
    };

  changeNotExecutedHistoyItemContent: (
    o: ImmoIPSubProjectChangesObject,
    s: string
  ) => void = (o, s) => {
    o.notExecutedHistoryItemObject.content = s;
  };

  changeNotExecutedHistoyItemCallClient: (
    o: ImmoIPSubProjectChangesObject,
    s: boolean
  ) => void = (o, s) => {
    o.notExecutedHistoryItemObject.callClient = s;
  };

  changeNotExecutedHistoyItemClientCommunicationSummary: (
    o: ImmoIPSubProjectChangesObject,
    s: string
  ) => void = (o, s) => {
    o.notExecutedHistoryItemObject.clientCommunicationSummary = s;
  };

  changeNotExecutedHistoyItemCallInpress: (
    o: ImmoIPSubProjectChangesObject,
    s: boolean
  ) => void = (o, s) => {
    o.notExecutedHistoryItemObject.callInpress = s;
  };

  changeNotExecutedHistoyItemInpressCommunicationSummary: (
    o: ImmoIPSubProjectChangesObject,
    s: string
  ) => void = (o, s) => {
    o.notExecutedHistoryItemObject.inpressCommunicationSummary = s;
  };

  changeNotExecutedHistoyItemSuggestedFollowUp: (
    o: ImmoIPSubProjectChangesObject,
    s: string
  ) => void = (o, s) => {
    o.notExecutedHistoryItemObject.suggestedFollowUp = s;
  };

  changeNotExecutedHistoyItemStockAtBranch: (
    o: ImmoIPSubProjectChangesObject,
    s: number
  ) => void = (o, s) => {
    o.notExecutedHistoryItemObject.stockAtBranchId = s;
  };

  changeNotExecutedHistoyItemOnlineInfo: (
    o: ImmoIPSubProjectChangesObject,
    s: string
  ) => void = (o, s) => {
    o.notExecutedHistoryItemObject.onlineInfo = s;
  };

  private setProductExecutedUnbound: (
    o: ImmoIPProductChangesObject,
    p: ProductExecutedParameterObject
  ) => void = (o, p) => {
    o.executionComplete = true;
    o.executionDate = p.executionDate;
    o.executionTime = p.executionTime;
    this.setStoringProductChange(o);
  };

  setProductExecuted: (
    o: ImmoIPProductChangesObject,
    p: ProductExecutedParameterObject
  ) => void = this.setProductExecutedUnbound.bind(this);

  private setProductNotExecutedUnbound: (
    o: ImmoIPProductChangesObject,
    dummy: any
  ) => void = (o, dummy) => {
    o.executionComplete = false;
    o.executionDate = null;
    o.executionTime = null;
    this.setStoringProductChange(o);
  };

  setProductNotExecuted: (o: ImmoIPProductChangesObject, dummy: any) => void =
    this.setProductNotExecutedUnbound.bind(this);

  private resetProductNotExecutedUnbound: (
    o: ImmoIPProductChangesObject,
    dummy: any
  ) => void = (o, dummy) => {
    o.executionComplete = null;
    o.executionDate = null;
    o.executionTime = null;
    this.setStoringProductChange(o);
  };

  resetProductNotExecuted: (o: ImmoIPProductChangesObject, dummy: any) => void =
    this.resetProductNotExecutedUnbound.bind(this);

  private setStoringProductChange(o: ImmoIPProductChangesObject): void {
    const storingProductChangeSubject = this.storingProductChange.get(o.id);
    if (storingProductChangeSubject != null) {
      storingProductChangeSubject.next(true);
    }
  }

  changePlacerFeedback: (o: ImmoIPSubProjectChangesObject, s: string) => void =
    (o, s) => {
      o.placerFeedback = s;
    };

  changeInternalInfo: (
    o: ImmoIPSubProjectChangesObject,
    s: {
      info: string;
      recurring: boolean;
    }
  ) => void = (o, s) => {
    o.internalInfo = s.info;
    o.recurringInternalInfo = s.recurring;
  };

  changePlacementConfig: (
    o: ImmoIPSubProjectChangesObject,
    s: {
      boardType: BoardType;
      placementType: PlacementType;
    }
  ) => void = (o, s) => {
    o.boardType = s.boardType;
    o.placementType = s.placementType;
  };

  changeAddress: (
    o: ImmoIPSubProjectChangesObject,
    s: {
      address: Address;
    }
  ) => void = (o, s) => {
    o.address = s.address;
  };

  private setSubProjectExecutedUnbound: (
    o: ImmoIPSubProjectChangesObject,
    p: SubProjectExecutedParameterObject
  ) => void = (o, p) => {
    o.isChange = false;
    o.currentExecutionDate = p.date;
    if (!p.baseProject) {
      o.placementDone = true;
    }
    if (o.registeredActions == null) {
      o.registeredActions = [];
    }
    o.registeredActions.unshift(p.registeredAction);
    if (o.notExecutedHistoryItemObject != null) {
      this.immoIPSubProjectChangesService.cleanupNotExecutedHistoryItemAssets(
        o.notExecutedHistoryItemObject.offlineAssets
      );
      delete o.notExecutedHistoryItemObject;
    }
    this.storingSubProjectChange.next(true);
  };

  setSubProjectExecuted: (
    o: ImmoIPSubProjectChangesObject,
    p: SubProjectExecutedParameterObject
  ) => void = this.setSubProjectExecutedUnbound.bind(this);

  private setSubProjectNotExecutedUnbound: (
    o: ImmoIPSubProjectChangesObject,
    p: SubProjectNotExecutedParameterObject
  ) => void = (o, p) => {
    o.isChange = false;
    o.currentExecutionDate = null;
    if (!p.baseProject) {
      o.placementDone = false;
    }
    if (p.notExecutedHistoryItemObject != null) {
      o.notExecutedHistoryItemObject = p.notExecutedHistoryItemObject;
    }
    if (o.registeredActions == null) {
      o.registeredActions = [];
    }
    o.registeredActions.unshift(p.registeredAction);
    this.storingSubProjectChange.next(true);
  };

  setSubProjectNotExecuted: (
    o: ImmoIPSubProjectChangesObject,
    p: SubProjectNotExecutedParameterObject
  ) => void = this.setSubProjectNotExecutedUnbound.bind(this);

  private resetUnbound: (
    o: ImmoIPSubProjectChangesObject,
    p: ResetSubProjectNotExecutedParameterObject
  ) => void = (o, p) => {
    o.isChange = false;
    if (!p.baseProject) {
      o.placementDone = null;
    }
    if (o.notExecutedHistoryItemObject != null) {
      if (
        CollectionHelper.isPopulated(
          o.notExecutedHistoryItemObject.offlineAssets
        )
      ) {
        this.immoIPSubProjectChangesService.cleanupNotExecutedHistoryItemAssets(
          o.notExecutedHistoryItemObject.offlineAssets
        );
      }
      delete o.notExecutedHistoryItemObject;
    }
    this.storingSubProjectChange.next(true);
  };

  reset: (
    o: ImmoIPSubProjectChangesObject,
    p: ResetSubProjectNotExecutedParameterObject
  ) => void = this.resetUnbound.bind(this);

  private setSubProjectChangesUnbound: (
    o: ImmoIPSubProjectChangesObject,
    p: SubmitSubProjectChangesParameterObject
  ) => void = (o, p) => {
    o.isChange = true;
    if (p.notExecutedHistoryItemObject != null) {
      o.notExecutedHistoryItemObject = p.notExecutedHistoryItemObject;
    }
    this.storingSubProjectChange.next(true);
  };

  setSubProjectChanges: (
    o: ImmoIPSubProjectChangesObject,
    p: SubmitSubProjectChangesParameterObject
  ) => void = this.setSubProjectChangesUnbound.bind(this);

  changeProductToDoItem: (
    o: ImmoIPProductChangesObject,
    t: ToDoItem,
    prop: string
  ) => void = (o, t, prop) => {
    o[prop] = t;
  };

  changeSubProjectToDoItem: (
    o: ImmoIPSubProjectChangesObject,
    t: ToDoItem,
    prop: string
  ) => void = (o, t, prop) => {
    o[prop] = t;
  };

  productAssetAdded: (
    o: ImmoIPProductChangesObject,
    productAssets: ProductAsset[]
  ) => void = (o, productAssets) => {
    this.generalAssetAddedFunction(o, productAssets);
  };

  projectPlacementAssetAdded: (
    o: ImmoIPSubProjectChangesObject,
    productAssets: ProductAsset[]
  ) => void = (o, productAssets) => {
    this.generalAssetAddedFunction(o, productAssets);
  };

  private generalAssetAddedFunction: (
    o: { offlineAssets?: OfflineAsset[] },
    productAssets: ProductAsset[]
  ) => void = (o, productAssets) => {
    if (CollectionHelper.isPopulated(productAssets)) {
      productAssets.forEach((pa) => {
        if (
          pa.asset.localFileUuid != null &&
          !this.offlineAssetsSet.has(pa.asset.localFileUuid) &&
          pa.id == null
        ) {
          if (o.offlineAssets == null) {
            o.offlineAssets = [];
          }
          o.offlineAssets.push({
            name: pa.asset.name,
            type: pa.asset.type,
            formSection: pa.formSection,
            localFileUuid: pa.asset.localFileUuid,
          });
          this.offlineAssetsSet.add(pa.asset.localFileUuid);
        }
      });
    }
  };

  private generalAssetDeletedFunction: (
    formSection: string,
    o: { offlineAssets?: OfflineAsset[]; assets?: ProductAsset[] },
    productAssets: ProductAsset[]
  ) => void = (formSection, o, productAssets) => {
    const toDeleteLocalAssets =
      o.offlineAssets == null
        ? []
        : !CollectionHelper.isPopulated(productAssets)
        ? [...o.offlineAssets]
        : this.calculateDiff(o.offlineAssets, productAssets, formSection);

    const productAssetIds = !CollectionHelper.isPopulated(productAssets)
      ? null
      : productAssets.filter((p) => p.id != null).map((p) => p.id);
    const toDeleteAssets =
      o.assets == null
        ? []
        : !CollectionHelper.isPopulated(productAssetIds)
        ? [...o.assets]
        : o.assets.filter((a) => !productAssetIds.includes(a.id));

    if (toDeleteLocalAssets.length > 0 || toDeleteAssets.length > 0) {
      toDeleteLocalAssets.forEach((a: OfflineAsset) => {
        this.offlineAssetsSet.delete(a.localFileUuid);
      });
      const toDeleteLocalUuids: string[] = [];
      toDeleteLocalAssets.forEach((a) => {
        toDeleteLocalUuids.push(a.localFileUuid);
        if (a.uuid != null) {
          if (this.subProjectChanges.toDeleteAssetUuids == null) {
            this.subProjectChanges.toDeleteAssetUuids = [];
          }
          this.subProjectChanges.toDeleteAssetUuids.push(a.uuid);
          this.storingOfflineChange.next(true);
          this.subProjectStoreTriggerSubject.next(this.subProjectChanges);
        }
      });
      CollectionHelper.filterInPlace(
        o.offlineAssets,
        (a) => !toDeleteLocalUuids.includes(a.localFileUuid)
      );
      toDeleteAssets.forEach((a) => {
        if (a.asset != null && a.asset.uuid != null) {
          if (this.subProjectChanges.toDeleteAssetUuids == null) {
            this.subProjectChanges.toDeleteAssetUuids = [];
          }
          this.subProjectChanges.toDeleteAssetUuids.push(a.asset.uuid);
          this.storingOfflineChange.next(true);
          this.subProjectStoreTriggerSubject.next(this.subProjectChanges);
        }
      });
      if (
        CollectionHelper.isPopulated(this.subProjectChanges.toDeleteAssetUuids)
      ) {
        this.submitDeleteAssets();
      }
    }
  };

  productAssetDeletedFunction = partial(this.generalAssetDeletedFunction, [
    'execution',
  ]);

  adjustmentAssetDeletedFunction = partial(this.generalAssetDeletedFunction, [
    'adjustmentPlacementSuccess',
  ]);

  private submitting: boolean = false;
  private storingOfflineChange: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private storingAnyOfflineChange: Subject<boolean> = new Subject();
  private subscriptions: Subscription[] = [];

  private constructor(
    private dateString: string,
    private subProjectChanges: ImmoIPSubProjectChangesObject,
    private subProjectStoreTriggerSubject: Subject<ImmoIPSubProjectChangesObject>,
    private storingSubProjectChange: Subject<boolean>,
    private productChangesMap: Map<number, ImmoIPProductChangesObject>,
    private productChangesStoreTriggerSubjects: Map<
      number,
      Subject<ImmoIPProductChangesObject>
    >,
    private storingProductChange: Map<number, Subject<boolean>>,
    private offlineAssetsSet: Set<string>,
    private ngForage: NgForage,
    private immoIPSubProjectChangesService: ImmoIPSubProjectChangesService,
    private immoIPProductChangesService: ImmoIPProductChangesService,
    private immoIPPlacerSubmitService: ImmoIPPlacerSubmitService,
    private immoIPPlacerSubmitQueueService: ImmoIPPlacerSubmitQueueService,
    private snackService: SnackService
  ) {
    this.initializeStoringAnyOfflineChangeSubject();
    this.subscriptions.push(
      this.subProjectStoreTriggerSubject
        .pipe(
          debounceTime(
            ImmoIPPlacerTourItemDetailChangeStorageHandler.STORE_OFFLINE_CHANGE_DELAY
          )
        )
        .subscribe({
          next: (subProjectChanges) =>
            this.doStoreSubProjectChanges(subProjectChanges),
        })
    );
    if (
      productChangesStoreTriggerSubjects != null &&
      productChangesStoreTriggerSubjects.size > 0
    ) {
      productChangesStoreTriggerSubjects.forEach((s) =>
        this.subscriptions.push(
          s
            .pipe(
              debounceTime(
                ImmoIPPlacerTourItemDetailChangeStorageHandler.STORE_OFFLINE_CHANGE_DELAY
              )
            )
            .subscribe({
              next: (productChanges) =>
                this.doStoreProductChanges(productChanges),
            })
        )
      );
    }
  }

  private initializeStoringAnyOfflineChangeSubject(): void {
    if (
      this.storingProductChange == null ||
      this.storingProductChange.size === 0
    ) {
      this.storingAnyOfflineChange = this.storingSubProjectChange;
      return;
    }
    const toCombine = [
      this.storingSubProjectChange,
      ...this.storingProductChange.values(),
    ];
    combineLatest(toCombine)
      .pipe(mergeMap((arr) => of(arr.reduce((b1, b2) => b1 || b2))))
      .subscribe(this.storingAnyOfflineChange);
    // combineLatest does not emit an initial value until every input observable emits at least one value, so do this manually
    this.storingOfflineChange.next(false);
    this.storingSubProjectChange.next(false);
    this.storingProductChange.forEach((s) => s.next(false));
  }

  private doStoreSubProjectChanges(
    subProjectChanges: ImmoIPSubProjectChangesObject
  ): void {
    this.storingOfflineChange.next(true);
    this.storingSubProjectChange.next(true);
    this.immoIPSubProjectChangesService
      .storeObject(subProjectChanges)
      .catch((err) => console.error(err))
      .finally(() => {
        this.storingOfflineChange.next(false);
        this.storingSubProjectChange.next(false);
      });
  }

  private storeCurrentSubProjectChanges(): Promise<void> {
    this.storingOfflineChange.next(true);
    this.storingSubProjectChange.next(true);
    return this.immoIPSubProjectChangesService
      .storeObject(this.subProjectChanges)
      .catch((err) => console.error(err))
      .finally(() => {
        this.storingOfflineChange.next(false);
        this.storingSubProjectChange.next(false);
      });
  }

  private doStoreProductChanges(
    productChanges: ImmoIPProductChangesObject
  ): void {
    const storingProductChangeSubject = this.storingProductChange.get(
      productChanges.id
    );
    if (storingProductChangeSubject != null) {
      storingProductChangeSubject.next(true);
    }
    this.storingOfflineChange.next(true);
    this.immoIPProductChangesService
      .storeObject(productChanges)
      .catch((err) => console.error(err))
      .finally(() => {
        this.storingOfflineChange.next(false);
        if (storingProductChangeSubject != null) {
          storingProductChangeSubject.next(false);
        }
      });
  }

  public destroy(): void {
    this.subProjectChanges = null;
    this.productChangesMap = null;
    if (CollectionHelper.isPopulated(this.subscriptions)) {
      this.subscriptions.forEach((s) => s.unsubscribe());
      this.subscriptions = null;
    }
    if (this.storingOfflineChange != null) {
      this.storingOfflineChange.complete();
      this.storingOfflineChange = null;
    }
    if (this.subProjectStoreTriggerSubject != null) {
      this.subProjectStoreTriggerSubject.complete();
      this.subProjectStoreTriggerSubject = null;
    }
    if (this.storingSubProjectChange != null) {
      this.storingSubProjectChange.complete();
      this.storingSubProjectChange = null;
    }
    if (
      this.productChangesStoreTriggerSubjects != null &&
      this.productChangesStoreTriggerSubjects.size > 0
    ) {
      this.productChangesStoreTriggerSubjects.forEach((s) => s.complete());
      this.productChangesStoreTriggerSubjects = null;
    }
    if (
      this.storingProductChange != null &&
      this.storingProductChange.size > 0
    ) {
      this.storingProductChange.forEach((s) => s.complete());
      this.storingProductChange = null;
    }
    if (this.storingAnyOfflineChange != null) {
      this.storingAnyOfflineChange.complete();
      this.storingAnyOfflineChange = null;
    }
  }

  public getCurrentNotExecutedHistoryItemObject(): ImmoIPNotExecutedHistoryItemObject {
    if (
      this.subProjectChanges.notExecutedHistoryItemObject != null &&
      this.subProjectChanges.notExecutedHistoryItemObject.stockAtBranchId ==
        null &&
      this.subProjectChanges.notExecutedHistoryItemObject.stockAtBranch != null
    ) {
      this.subProjectChanges.notExecutedHistoryItemObject.stockAtBranchId =
        this.subProjectChanges.notExecutedHistoryItemObject.stockAtBranch.id;
    }
    return this.subProjectChanges.notExecutedHistoryItemObject;
  }

  public getNotExecutedHistoryItemObject(): ImmoIPNotExecutedHistoryItemObject {
    return this.subProjectChanges.notExecutedHistoryItemObject != null
      ? this.subProjectChanges.notExecutedHistoryItemObject
      : {
          offlineAssets: [],
        };
  }

  public storeOfflineSubProjectChange<T>(
    arg: T,
    changeFunction: (o: ImmoIPSubProjectChangesObject, a: T) => void
  ): void {
    changeFunction(this.subProjectChanges, arg);
    this.storingOfflineChange.next(true);
    this.subProjectStoreTriggerSubject.next(this.subProjectChanges);
  }

  public addRegisteredAction(registeredAction: RegisteredAction): void {
    if (registeredAction != null) {
      if (this.subProjectChanges.registeredActions == null) {
        this.subProjectChanges.registeredActions = [];
      }
      this.subProjectChanges.registeredActions.unshift(registeredAction);
      this.storingOfflineChange.next(true);
      this.subProjectStoreTriggerSubject.next(this.subProjectChanges);
    }
  }

  public storeOfflineProductChange<T>(
    product: Product,
    arg: T,
    changeFunction: (o: ImmoIPProductChangesObject, a: T) => void
  ): void {
    const productChanges = this.productChangesMap.get(product.id);
    changeFunction(productChanges, arg);
    this.storingOfflineChange.next(true);
    const subject = this.productChangesStoreTriggerSubjects.get(product.id);
    if (subject != null) {
      subject.next(productChanges);
    }
  }

  public fillProductToDoItem(
    productId: number,
    toDoItem: ToDoItem,
    prop: string
  ): void {
    const productChanges = this.productChangesMap.get(productId);
    if (
      productChanges != null &&
      GeneralHelper.hasOwnProperty(productChanges, prop)
    ) {
      const offlineToDoItem = <ToDoItem>productChanges[prop];
      toDoItem.done = offlineToDoItem.done;
    }
  }

  public storeProductToDoItem(
    product: Product,
    toDoItem: ToDoItem,
    prop: string,
    changeFunction: (
      o: ImmoIPProductChangesObject,
      toDoItem: ToDoItem,
      prop: string
    ) => void
  ): void {
    const productChanges = this.productChangesMap.get(product.id);
    changeFunction(productChanges, toDoItem, prop);
    this.storingOfflineChange.next(true);
    const subject = this.productChangesStoreTriggerSubjects.get(product.id);
    if (subject != null) {
      subject.next(productChanges);
    }
  }

  public fillSubProjectToDoItem(toDoItem: ToDoItem, prop: string): void {
    if (GeneralHelper.hasOwnProperty(this.subProjectChanges, prop)) {
      const offlineToDoItem = <ToDoItem>this.subProjectChanges[prop];
      toDoItem.done = offlineToDoItem.done;
    }
  }

  public storeSubProjectToDoItem(
    toDoItem: ToDoItem,
    prop: string,
    changeFunction: (
      o: ImmoIPSubProjectChangesObject,
      toDoItem: ToDoItem,
      prop: string
    ) => void
  ): void {
    changeFunction(this.subProjectChanges, toDoItem, prop);
    this.storingOfflineChange.next(true);
    this.subProjectStoreTriggerSubject.next(this.subProjectChanges);
  }

  public resetToDoItems(): void {
    const hasAnyChangesObject =
      this.subProjectChanges != null || this.productChangesMap.size > 0;
    if (this.subProjectChanges != null) {
      for (const key of Object.keys(this.subProjectChanges)) {
        if (isSubProjectToDoItemProp(key)) {
          delete this.subProjectChanges[key];
        }
      }
      this.subProjectStoreTriggerSubject.next(this.subProjectChanges);
    }
    if (this.productChangesMap.size > 0) {
      for (let [id, productChangesObject] of this.productChangesMap) {
        for (const key of Object.keys(productChangesObject)) {
          if (isProductToDoItemProp(key)) {
            delete productChangesObject[key];
          }
        }
        const subject = this.productChangesStoreTriggerSubjects.get(id);
        if (subject != null) {
          subject.next(productChangesObject);
        }
      }
    }
    if (hasAnyChangesObject) {
      this.storingOfflineChange.next(true);
    }
  }

  public getSubProjectToDoItem(prop: string): ToDoItem {
    if (GeneralHelper.hasOwnProperty(this.subProjectChanges, prop)) {
      return <ToDoItem>this.subProjectChanges[prop];
    }
    return null;
  }

  private calculateDiff(
    offlineAssets: OfflineAsset[],
    productAssets: ProductAsset[],
    formSection: string
  ): OfflineAsset[] {
    return _.differenceBy(
      offlineAssets,
      productAssets.filter(
        (pa) =>
          pa != null &&
          pa.asset != null &&
          pa.formSection === formSection &&
          pa.asset.localFileUuid != null
      ),
      ImmoIPPlacerTourItemDetailChangeStorageHandler.differenceByIteratee
    );
  }

  private static differenceByIteratee(i: OfflineAsset | ProductAsset): string {
    if (AssetHelper.isProductAsset(i)) {
      return i.asset.localFileUuid;
    } else {
      return i.localFileUuid;
    }
  }

  public deleteNotExecutedHistoryItemAsset(uuid: string): void {
    if (!StringHelper.isEmpty(uuid)) {
      if (this.subProjectChanges.toDeleteAssetUuids == null) {
        this.subProjectChanges.toDeleteAssetUuids = [];
      }
      this.subProjectChanges.toDeleteAssetUuids.push(uuid);
    }

    this.storingOfflineChange.next(true);
    this.subProjectStoreTriggerSubject.next(this.subProjectChanges);

    this.submitDeleteAssets();
  }

  public async submitRegisteredActions(): Promise<void> {
    await ObservableHelper.untilFirstSatisfying(
      this.storingOfflineChange,
      (e) => e === false
    ).toPromise();
    await this.immoIPPlacerSubmitService.submitRegisteredActions(
      this.subProjectChanges,
      this.dateString
    );
  }

  public async submitSubProject(executed: boolean): Promise<void> {
    this.submitting = true;
    try {
      await ObservableHelper.untilFirstSatisfying(
        this.storingAnyOfflineChange,
        (e) => e === false
      ).toPromise();
      await this.immoIPPlacerSubmitService.submitSubProject(
        this.subProjectChanges,
        this.dateString
      );
      if (this.subProjectChanges.isChange) {
        this.snackService.showSuccess(`Wijzigingen succesvol opgeslagen`);
      } else {
        this.snackService.showSuccess(
          `Opdracht succesvol op${executed ? '' : ' niet '} uitgevoerd gezet`
        );
      }
    } finally {
      this.submitting = false;
    }
  }

  public async submitReset(executed: boolean): Promise<void> {
    delete this.subProjectChanges.currentExecutionDate;
    this.submitting = true;
    try {
      await ObservableHelper.untilFirstSatisfying(
        this.storingAnyOfflineChange,
        (e) => e === false
      ).toPromise();
      const submitTypeFullIsPresent =
        await this.immoIPPlacerSubmitQueueService.submitTypeIsPresentForSubProject(
          this.subProjectChanges.id,
          ImmoIPSubmitType.Full
        );
      if (submitTypeFullIsPresent) {
        await this.immoIPPlacerSubmitQueueService.removeTypeFromSubmitEntry(
          this.subProjectChanges.id,
          ImmoIPSubmitType.Full
        );
      } else {
        await this.immoIPPlacerSubmitService.reset(
          this.subProjectChanges,
          this.dateString,
          executed
        );
      }
      const snackMsg = executed
        ? 'Uitgevoerd succesvol gerest'
        : 'Niet uitgevoerd succesvol gereset';
      this.snackService.showSuccess(snackMsg);
    } finally {
      this.submitting = false;
    }
  }

  public async directlySubmitExecutionAssets(): Promise<void> {
    this.submitting = true;
    try {
      await ObservableHelper.untilFirstSatisfying(
        this.storingOfflineChange,
        (e) => e === false
      ).toPromise();
      const submitTypeFullIsPresent =
        await this.immoIPPlacerSubmitQueueService.submitTypeIsPresentForSubProject(
          this.subProjectChanges.id,
          ImmoIPSubmitType.Full
        );
      if (!submitTypeFullIsPresent) {
        await this.immoIPPlacerSubmitService.directlySubmitOfflineAssets(
          this.subProjectChanges,
          this.dateString,
          true
        );
      }
    } finally {
      this.submitting = false;
    }
  }

  public directlySubmitNotExecutedAssets(): Promise<void> {
    this.submitting = true;
    this.storingOfflineChange.next(true);
    this.subProjectStoreTriggerSubject.next(this.subProjectChanges);
    return this.doDirectlySubmitNotExecutedAssets();
  }

  private async doDirectlySubmitNotExecutedAssets(): Promise<void> {
    try {
      await ObservableHelper.untilFirstSatisfying(
        this.storingOfflineChange,
        (e) => e === false
      ).toPromise();
      const submitTypeFullIsPresent =
        await this.immoIPPlacerSubmitQueueService.submitTypeIsPresentForSubProject(
          this.subProjectChanges.id,
          ImmoIPSubmitType.Full
        );
      if (!submitTypeFullIsPresent) {
        await this.immoIPPlacerSubmitService.directlySubmitNotExecutedAssets(
          this.subProjectChanges,
          this.dateString,
          true
        );
      }
    } finally {
      this.submitting = false;
    }
  }

  private async submitDeleteAssets(): Promise<void> {
    if (
      !CollectionHelper.isPopulated(this.subProjectChanges.toDeleteAssetUuids)
    ) {
      return;
    }

    this.submitting = true;

    try {
      await ObservableHelper.untilFirstSatisfying(
        this.storingOfflineChange,
        (e) => e === false
      ).toPromise();
      await this.immoIPPlacerSubmitService.deleteAssets(
        this.subProjectChanges,
        this.dateString
      );
    } finally {
      this.submitting = false;
    }
  }

  public async submitInternalInfo(): Promise<void> {
    this.submitting = true;
    try {
      await ObservableHelper.untilFirstSatisfying(
        this.storingAnyOfflineChange,
        (e) => e === false
      ).toPromise();
      await this.immoIPPlacerSubmitService.submitInternalInfo(
        this.subProjectChanges,
        this.dateString
      );
    } finally {
      this.submitting = false;
    }
  }

  public async submitPlacementConfig(): Promise<SubProject> {
    this.submitting = true;
    try {
      await ObservableHelper.untilFirstSatisfying(
        this.storingAnyOfflineChange,
        (e) => e === false
      ).toPromise();
      return this.immoIPPlacerSubmitService.submitPlacementConfig(
        this.subProjectChanges,
        this.dateString
      );
    } finally {
      this.submitting = false;
    }
  }

  public async submitAddress(): Promise<SubProject> {
    this.submitting = true;
    try {
      await ObservableHelper.untilFirstSatisfying(
        this.storingAnyOfflineChange,
        (e) => e === false
      ).toPromise();
      return this.immoIPPlacerSubmitService.submitAddress(
        this.subProjectChanges,
        this.dateString
      );
    } finally {
      this.submitting = false;
    }
  }

  public async reloadOfflineChangeObjects(
    subProject: SubProject
  ): Promise<void> {
    this.subProjectChanges =
      await ImmoIPPlacerTourItemDetailChangeStorageHandler.initializeSubProjectChanges(
        subProject,
        this.immoIPSubProjectChangesService,
        this.immoIPProductChangesService
      );

    this.offlineAssetsSet.clear();

    if (
      subProject.projectPlacement != null &&
      CollectionHelper.isPopulated(this.subProjectChanges.offlineAssets)
    ) {
      await ImmoIPPlacerTourItemDetailChangeStorageHandler.handleOfflineProjectPlacementAssets(
        subProject.projectPlacement,
        this.subProjectChanges.offlineAssets,
        this.offlineAssetsSet,
        this.ngForage
      );
    }
    if (
      subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
      subProject.project != null &&
      CollectionHelper.isPopulated(subProject.project.products)
    ) {
      await this.handleReloadProductChangeObjects(subProject);
    }
  }

  private async handleReloadProductChangeObjects(
    subProject: SubProject
  ): Promise<void> {
    const productIds = subProject.project.products.map((p) => p.id);
    CollectionHelper.filterInPlace(productIds, (id) => id != null);
    const productChangeObjects =
      await this.immoIPProductChangesService.getObjects(productIds);
    this.productChangesMap.clear();
    productChangeObjects.forEach((p) => this.productChangesMap.set(p.id, p));
    for (const p of subProject.project.products) {
      await this.handleReloadProductChangeObjectForProduct(p);
    }
  }

  private async handleReloadProductChangeObjectForProduct(p: Product) {
    let changeObject: ImmoIPProductChangesObject = this.productChangesMap.get(
      p.id
    );
    if (changeObject == null) {
      changeObject = {
        id: p.id,
      };
      this.productChangesMap.set(p.id, changeObject);
    }
    if (CollectionHelper.isPopulated(changeObject.offlineAssets)) {
      await ImmoIPPlacerTourItemDetailChangeStorageHandler.handleOfflineProductAssets(
        p,
        changeObject.offlineAssets,
        this.offlineAssetsSet,
        this.ngForage
      );
    }
  }

  public resetRegisteredActions(store = false): Promise<void> {
    if (
      !CollectionHelper.isPopulated(this.subProjectChanges.registeredActions)
    ) {
      return;
    }
    this.subProjectChanges.registeredActions.length = 0;
    return store ? this.storeCurrentSubProjectChanges() : Promise.resolve();
  }

  public applyModifiedConfigToSubProject(subProject: SubProject): void {
    OfflineStorageTourModifiedConfigHelper.applyModifiedConfig(
      subProject,
      this.subProjectChanges
    );
  }

  public get isSubmitting(): boolean {
    return this.submitting;
  }

  public get isStoringOfflineChange(): boolean {
    return this.storingOfflineChange.getValue();
  }
}

interface ProductChangeVariables {
  productChangesMap: Map<number, ImmoIPProductChangesObject>;
  productChangesStoreTriggerSubjects: Map<
    number,
    Subject<ImmoIPProductChangesObject>
  >;
  storingProductChange: Map<number, Subject<boolean>>;
}

export interface ProductExecutedParameterObject {
  executionDate: Date;
  executionTime: string;
}

export interface SubProjectExecutedParameterObject {
  baseProject: boolean;
  date: Date;
  registeredAction: RegisteredAction;
}

export interface SubProjectNotExecutedParameterObject {
  baseProject: boolean;
  registeredAction: RegisteredAction;
  notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject;
}

export interface ResetSubProjectNotExecutedParameterObject {
  baseProject: boolean;
}

export interface SubmitSubProjectChangesParameterObject {
  notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject;
}
